import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { createBrowserRouter, RouterProvider } from 'react-router';
import ErrorPage from '@@components/ErrorPage/ErrorPage';
import Landing from '@@components/Landing/Landing';
import Results from '@@components/Results/Results';
import { reactResultsLoader } from '@@components/Results/Results.loader';
import Root from '@@components/Root/Root';
import '@gialaboratory/sdk/theme.css';
import '@@/styles/scss/index.scss';
import '@@/styles/css/variables.css';
import '@@/styles/tailwind.css';
import '@@/styles/gia-header.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Creates a client with persistence configuration
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 2 * 60 * 60 * 1000, // 2h; must be >= persister maxAge
        },
    },
});

// Create persister
const persister = createSyncStoragePersister({
    storage: window.localStorage,
    key: 'rc-cache', // Unique key for application cache
});

// Persist buster: changes per build so deploys drop the stale persisted cache.
const buildId = process.env['BUILD_ID'] ?? 'dev';

const router = createBrowserRouter([
    {
        path: '/',
        element: <Root />,
        errorElement: <ErrorPage />,
        children: [
            {
                path: '/report-check-landing',
                element: <Landing />,
            },
            {
                path: '/report-check',
                element: <Results />,
                loader: reactResultsLoader,
            },
        ],
    },
]);

createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{ persister, buster: buildId, maxAge: 2 * 60 * 60 * 1000 }} // maxAge: 2 hours
        >
            <RouterProvider router={router} />
            <ReactQueryDevtools initialIsOpen={false} />
        </PersistQueryClientProvider>
    </React.StrictMode>
);
